import React, { useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, CircularProgress, TextField, Button, Box, Paper } from '@material-ui/core';

import { changeUserAgency, assignAgencyToCompany, changeUserDisplayName, upgradeUserToAdmin } from '../../../API/agent';
import PageContainer from '../../../myComponents/pageContainer';

const useStyles = makeStyles(theme => ({
  header: {
    color: 'black',
    textAlign: 'center',
    marginBottom: '50px',
    marginTop: '20px',
  },
  section: {
    width: '100%',
    maxWidth: 400,
    margin: '0 auto 40px auto',
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
    borderBottom: '2px solid gray',
  },
  input: {
    width: '100%',
    marginBottom: '10px',
  },
  button: {
    backgroundColor: '#19989B',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#157d80',
    },
    marginTop: theme.spacing(1),
  },
}));

const Admin = () => {
  const classes = useStyles();

  const [userEmail, setUserEmail] = useState('');
  const [targetAgencyUserEmail, setTargetAgencyUserEmail] = useState('');

  const [companyEmail, setCompanyEmail] = useState('');
  const [agencyEmail, setAgencyEmail] = useState('');

  const [displayNameUserEmail, setDisplayNameUserEmail] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');

  const [adminUpgradeUserEmail, setAdminUpgradeUserEmail] = useState('');

  const [loading, setLoading] = useState(false);

  const showToast = (type, message) => {
    toast[type](message, {
      position: 'bottom-right',
      autoClose: 4000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      style: type === 'success' ? { backgroundColor: '#19989B', color: '#fff' } : {},
    });
  };

  const changeUserAgencyHandler = async () => {
    if (!userEmail || !targetAgencyUserEmail) {
      showToast('warn', 'Please fill in both email fields.');
      return;
    }
    setLoading(true);
    try {
      const res = await changeUserAgency(userEmail, targetAgencyUserEmail);
      if (res.err) {
        showToast('warn', 'Something went wrong, please try again later.');
      } else {
        showToast('success', 'You have successfully changed the user agency.');
        setUserEmail('');
        setTargetAgencyUserEmail('');
      }
    } catch (err) {
      showToast('warn', 'Something went wrong, please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const assignAgencyToCompanyHandler = async () => {
    if (!companyEmail || !agencyEmail) {
      showToast('warn', 'Please fill in both email fields.');
      return;
    }
    setLoading(true);
    try {
      const res = await assignAgencyToCompany(companyEmail, agencyEmail);
      if (res.err) {
        showToast('warn', 'Something went wrong, please try again later.');
      } else {
        showToast('success', 'You have successfully assigned an agency to a company.');
        setCompanyEmail('');
        setAgencyEmail('');
      }
    } catch (err) {
      showToast('warn', 'Something went wrong, please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const changeUserDisplayNameHandler = async () => {
    if (!displayNameUserEmail || !newDisplayName) {
      showToast('warn', 'Please fill in both fields.');
      return;
    }
    setLoading(true);
    try {
      const res = await changeUserDisplayName(displayNameUserEmail, newDisplayName);
      if (res.err) {
        showToast('warn', 'Something went wrong, please try again later.');
      } else {
        showToast('success', 'You have successfully changed the user display name.');
        setDisplayNameUserEmail('');
        setNewDisplayName('');
      }
    } catch (err) {
      showToast('warn', 'Something went wrong, please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const upgradeUserToAdminHandler = async () => {
    if (!adminUpgradeUserEmail) {
      showToast('warn', 'Please fill in email field.');
      return;
    }
    setLoading(true);
    try {
      const res = await upgradeUserToAdmin(adminUpgradeUserEmail);
      if (res.err) {
        showToast('warn', 'Something went wrong, please try again later.');
      } else {
        showToast('success', 'You have successfully upgraded user to admin.');
        setAdminUpgradeUserEmail('');
      }
    } catch (err) {
      showToast('warn', 'Something went wrong, please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Typography variant="h1" className={classes.header}>
        Welcome to the All Mighty ADMIN page
      </Typography>
      <Paper className={classes.section}>
        <Typography variant="h2" style={{ fontWeight: 600, marginBottom: '20px' }}>
          Change user agency
        </Typography>

        <TextField
          label="User mail"
          variant="outlined"
          size="small"
          type="text"
          className={classes.input}
          helperText="Who do you want to assign to an agency?"
          value={userEmail}
          onChange={e => setUserEmail(e.target.value)}
        />
        <TextField
          label="Any agency-user mail"
          variant="outlined"
          size="small"
          className={classes.input}
          helperText="Provide a mail from any user that is assigned to the target agency"
          value={targetAgencyUserEmail}
          onChange={e => setTargetAgencyUserEmail(e.target.value)}
        />

        <Button variant="contained" className={classes.button} disabled={loading} onClick={changeUserAgencyHandler}>
          {loading ? <CircularProgress size={24} style={{ color: '#fff' }} /> : 'Submit'}
        </Button>
      </Paper>
      <Paper className={classes.section}>
        <Typography variant="h2" style={{ fontWeight: 600, marginBottom: '20px' }}>
          Assign agency to a company
        </Typography>

        <TextField
          label="User mail"
          variant="outlined"
          size="small"
          className={classes.input}
          helperText="Who do you want to turn into a company or expand?"
          value={companyEmail}
          onChange={e => setCompanyEmail(e.target.value)}
        />
        <TextField
          label="Any user-agency mail"
          variant="outlined"
          size="small"
          className={classes.input}
          helperText="Provide a mail from any user that is assigned to the target agency"
          value={agencyEmail}
          onChange={e => setAgencyEmail(e.target.value)}
        />

        <Button variant="contained" className={classes.button} disabled={loading} onClick={assignAgencyToCompanyHandler}>
          {loading ? <CircularProgress size={24} style={{ color: '#fff' }} /> : 'Submit'}
        </Button>
      </Paper>

      <Paper className={classes.section}>
        <Typography variant="h2" style={{ fontWeight: 600, marginBottom: '20px' }}>
          Change user display name
        </Typography>

        <TextField
          label="User mail"
          variant="outlined"
          size="small"
          type="text"
          className={classes.input}
          helperText="Whose display name do ou want to change?"
          value={displayNameUserEmail}
          onChange={e => setDisplayNameUserEmail(e.target.value)}
        />
        <TextField
          label="Display name"
          variant="outlined"
          size="small"
          className={classes.input}
          helperText="What display name should the user have?"
          value={newDisplayName}
          onChange={e => setNewDisplayName(e.target.value)}
        />

        <Button variant="contained" className={classes.button} disabled={loading} onClick={changeUserDisplayNameHandler}>
          {loading ? <CircularProgress size={24} style={{ color: '#fff' }} /> : 'Submit'}
        </Button>
      </Paper>
      <Paper className={classes.section}>
        <Typography variant="h2" style={{ fontWeight: 600, marginBottom: '20px' }}>
          Upgrade user to ADMIN
        </Typography>

        <TextField
          label="User mail"
          variant="outlined"
          size="small"
          type="text"
          className={classes.input}
          helperText="Who do you want to upgrade to ADMIN?"
          value={adminUpgradeUserEmail}
          onChange={e => setAdminUpgradeUserEmail(e.target.value)}
        />

        <Button variant="contained" className={classes.button} disabled={loading} onClick={upgradeUserToAdminHandler}>
          {loading ? <CircularProgress size={24} style={{ color: '#fff' }} /> : 'Submit'}
        </Button>
      </Paper>
      <ToastContainer
        theme="colored"
        position="bottom-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </PageContainer>
  );
};

export default Admin;
